import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  SkillGroup,
  SkillRequirementDeleteCommand,
  SkillRequirementUpdateCommand,
  SkillSpecializationResponse,
  SkillTrainingRequirementDeleteParams,
  SkillTrainingRequirementUpdateRequest,
  SkillTrainingRequirementScope,
} from '../../../models/skill-learning.model';
import {
  SKILL_DEPARTMENT_QUERY_PARAM,
  SKILL_LEARNING_EMPTY_COUNT,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_REQUIREMENT_SCOPE,
  SKILL_SPECIALIZATION_STATUS,
  SKILL_VALUE_TYPE,
} from '../../../options/skill-learning.option';

// TypeScript 2.3 needs a local alias to emit safe metadata for nullable interface inputs.
type NullableSpecialization = SkillSpecializationResponse | null;

@Component({
  selector: 'app-specialization-requirements-editor',
  templateUrl: './specialization-requirements-editor.component.html',
  styleUrls: ['./specialization-requirements-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecializationRequirementsEditorComponent implements OnChanges {

  readonly scopes = SKILL_REQUIREMENT_SCOPE;

  @ViewChild('requirementsBlock') requirementsBlock: ElementRef;

  @Input() specialization: NullableSpecialization = null;
  @Input() availableGroups: SkillGroup[] = [];
  @Input() selectedDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  @Input() isSubmitting = false;

  @Output() requirementUpdated = new EventEmitter<SkillRequirementUpdateCommand>();
  @Output() requirementDeleted = new EventEmitter<SkillRequirementDeleteCommand>();

  companyEnabled = false;
  companyLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;
  departmentEnabled = false;
  departmentLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;
  currentHeadEnabled = false;
  currentHeadLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;
  currentHeadDirty = false;
  private initialCurrentHeadEnabled = false;
  private initialCurrentHeadLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;
  companyDirty = false;
  departmentDirty = false;
  pendingScope: SkillTrainingRequirementScope | null = null;

  private initialCompanyEnabled = false;
  private initialCompanyLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;
  private initialDepartmentEnabled = false;
  private initialDepartmentLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;

  constructor(private changeDetector: ChangeDetectorRef) {}

  finishPending(): void {
    this.pendingScope = null;
    this.changeDetector.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.specialization || changes.selectedDepartmentId) {
      this.resetFromSpecialization();
    }
  }

  get isPublished(): boolean {
    return !!this.specialization
      && this.specialization.status === SKILL_SPECIALIZATION_STATUS.READY_FOR_LEARNING
      && this.publishedLevelCount >= SKILL_LEARNING_LEVEL_COUNT.ONE;
  }

  get hasRevision(): boolean {
    return !!this.specialization
      && typeof this.specialization.configurationRevision === SKILL_VALUE_TYPE.NUMBER;
  }

  get publishedLevelCount(): number {
    const value = this.specialization && this.specialization.publishedLevelCount
      || SKILL_LEARNING_EMPTY_COUNT;
    return Math.min(
      SKILL_LEARNING_LEVEL_COUNT.THREE,
      Math.max(SKILL_LEARNING_EMPTY_COUNT, value),
    );
  }

  get publishedLevelOptions(): number[] {
    const result: number[] = [];
    for (let level = SKILL_LEARNING_LEVEL_COUNT.ONE;
         level <= this.publishedLevelCount; level += SKILL_LEARNING_LEVEL_COUNT.ONE) {
      result.push(level);
    }
    return result;
  }

  get canManageCompany(): boolean {
    return this.canSetCompany || this.canDeleteCompany;
  }

  get canSetCompany(): boolean {
    return !!this.specialization && !!this.specialization.canManageCompanyProbationRequirement;
  }

  get canDeleteCompany(): boolean {
    return !!this.specialization && !!this.specialization.canDeleteCompanyProbationRequirement;
  }

  get canManageDepartment(): boolean {
    return !!this.specialization && !!this.specialization.canManageDepartmentProbationRequirement;
  }

  get canManageCurrentHead(): boolean {
    return !!this.specialization && !!this.specialization.canManageCurrentHeadRequirement;
  }

  get isDepartmentRequirementOverridden(): boolean {
    return !!this.selectedDepartmentId
      && !!this.specialization
      && this.specialization.probationRequiredByCompany === true;
  }

  get hasDirtyDepartmentRequirement(): boolean {
    return this.companyDirty || this.departmentDirty || this.currentHeadDirty;
  }

  get hasVisibleRequirements(): boolean {
    return this.canManageCurrentHead || this.currentHeadEnabled
      || this.canManageCompany || this.canManageDepartment
      || this.companyEnabled || this.departmentEnabled
      || !!(this.specialization && this.specialization.effectiveProbationRequiredLevel);
  }

  get canToggleCompany(): boolean {
    return this.hasRevision && !this.isSubmitting && !this.pendingScope
      && (this.initialCompanyEnabled
        ? this.canDeleteCompany : this.isPublished && this.canSetCompany);
  }

  get canEditCompanyLevel(): boolean {
    return this.hasRevision && this.isPublished && this.canSetCompany
      && !this.isSubmitting && !this.pendingScope;
  }

  get canToggleDepartment(): boolean {
    return this.hasRevision && !!this.selectedDepartmentId && this.canManageDepartment
      && !this.isSubmitting && !this.pendingScope
      && (this.isPublished || this.initialDepartmentEnabled);
  }

  get canToggleCurrentHead(): boolean {
    return this.hasRevision && this.canManageCurrentHead && !this.isSubmitting && !this.pendingScope
      && (this.isPublished || this.initialCurrentHeadEnabled);
  }

  get canEditDepartmentLevel(): boolean {
    return this.canToggleDepartment && this.isPublished;
  }

  get canEditCurrentHeadLevel(): boolean {
    return this.hasRevision && this.isPublished && this.canManageCurrentHead
      && !this.isSubmitting && !this.pendingScope;
  }

  get canSaveCompany(): boolean {
    return this.companyDirty && this.canSaveScope(
      this.companyEnabled,
      this.initialCompanyEnabled,
      this.companyEnabled ? this.canSetCompany : this.canDeleteCompany,
    );
  }

  get canSaveDepartment(): boolean {
    return this.departmentDirty && !!this.selectedDepartmentId
      && this.canSaveScope(this.departmentEnabled, this.initialDepartmentEnabled, this.canManageDepartment);
  }

  get canSaveCurrentHead(): boolean {
    return this.currentHeadDirty && (!this.currentHeadEnabled || this.currentHeadLevel >= SKILL_LEARNING_LEVEL_COUNT.ONE)
      && this.canSaveScope(this.currentHeadEnabled, this.initialCurrentHeadEnabled, this.canManageCurrentHead);
  }

  get effectiveProbationRequiredLevel(): number {
    return this.specialization && this.specialization.effectiveProbationRequiredLevel
      || SKILL_LEARNING_EMPTY_COUNT;
  }

  onCompanyEnabledChanged(enabled: boolean): void {
    this.companyEnabled = enabled;
    this.updateCompanyDirty();
  }

  get inheritedDepartmentName(): string {
    const sourceId = this.specialization && this.specialization.inheritedFromDepartmentId;
    const source = this.availableGroups.find(group => group.id === sourceId);
    return source ? source.name : SKILL_LEARNING_EMPTY_TEXT;
  }

  onCompanyLevelChanged(level: string): void {
    this.companyLevel = Number(level);
    this.updateCompanyDirty();
  }

  onDepartmentEnabledChanged(enabled: boolean): void {
    this.departmentEnabled = enabled;
    this.updateDepartmentDirty();
  }

  onDepartmentLevelChanged(level: string): void {
    this.departmentLevel = Number(level);
    this.updateDepartmentDirty();
  }

  onCurrentHeadEnabledChanged(enabled: boolean): void {
    this.currentHeadEnabled = enabled;
    this.updateCurrentHeadDirty();
  }

  onCurrentHeadLevelChanged(level: string): void {
    this.currentHeadLevel = Number(level);
    this.updateCurrentHeadDirty();
  }

  saveCompany(): void {
    this.emitScope(this.scopes.COMPANY_PROBATION, this.companyEnabled, this.companyLevel);
  }

  saveDepartment(): void {
    this.emitScope(
      this.scopes.DEPARTMENT_PROBATION,
      this.departmentEnabled,
      this.departmentLevel,
    );
  }

  saveCurrentHead(): void {
    this.emitScope(this.scopes.CURRENT_HEAD, this.currentHeadEnabled, this.currentHeadLevel);
  }

  discardDepartmentChanges(): void {
    this.resetFromSpecialization();
  }

  focus(): void {
    const element = this.requirementsBlock && this.requirementsBlock.nativeElement;
    if (element) {
      element.focus();
    }
  }

  trackByLevel(index: number, level: number): number {
    return level;
  }

  isScopePending(scope: SkillTrainingRequirementScope): boolean {
    return this.pendingScope === scope;
  }

  private emitScope(scope: SkillTrainingRequirementScope, enabled: boolean, level: number): void {
    if (!this.specialization || !this.hasRevision || this.isSubmitting || this.pendingScope) {
      return;
    }
    if (!this.canEmitScope(scope, enabled)) {
      return;
    }
    this.pendingScope = scope;
    if (!enabled) {
      this.requirementDeleted.emit({ scope, params: this.buildDeleteParams(scope) });
      return;
    }
    this.requirementUpdated.emit({ scope, request: this.buildUpdateRequest(scope, level) });
  }

  private buildUpdateRequest(
    scope: SkillTrainingRequirementScope,
    level: number,
  ): SkillTrainingRequirementUpdateRequest {
    const request: SkillTrainingRequirementUpdateRequest = {
      requiredLevel: level,
      expectedRevision: this.specialization.configurationRevision,
    };
    if (scope === this.scopes.DEPARTMENT_PROBATION) {
      request[SKILL_DEPARTMENT_QUERY_PARAM] = this.selectedDepartmentId;
    }
    if (scope === this.scopes.COMPANY_PROBATION && this.selectedDepartmentId) {
      request.responseDepartmentId = this.selectedDepartmentId;
    }
    return request;
  }

  private buildDeleteParams(scope: SkillTrainingRequirementScope): SkillTrainingRequirementDeleteParams {
    const params: SkillTrainingRequirementDeleteParams = {
      expectedRevision: this.specialization.configurationRevision,
    };
    if (scope === this.scopes.DEPARTMENT_PROBATION) {
      params[SKILL_DEPARTMENT_QUERY_PARAM] = this.selectedDepartmentId;
    }
    if (scope === this.scopes.COMPANY_PROBATION && this.selectedDepartmentId) {
      params.responseDepartmentId = this.selectedDepartmentId;
    }
    return params;
  }

  private resetFromSpecialization(): void {
    const value = this.specialization;
    this.companyEnabled = !!value && (value.probationRequiredByCompany === true
      || !!value.companyProbationRequiredLevel);
    this.companyLevel = value && value.companyProbationRequiredLevel || SKILL_LEARNING_LEVEL_COUNT.ONE;
    this.departmentEnabled = this.hasDepartmentRequirement();
    this.departmentLevel = value && value.departmentProbationRequiredLevel || SKILL_LEARNING_LEVEL_COUNT.ONE;
    this.resetCurrentHead();
    this.initialCompanyEnabled = this.companyEnabled;
    this.initialCompanyLevel = this.companyLevel;
    this.initialDepartmentEnabled = this.departmentEnabled;
    this.initialDepartmentLevel = this.departmentLevel;
    this.resetDirtyFlags();
  }

  private resetDirtyFlags(): void {
    this.companyDirty = false;
    this.departmentDirty = false;
    this.pendingScope = null;
  }

  private resetCurrentHead(): void {
    this.currentHeadEnabled = !!this.specialization && !!this.specialization.currentHeadOnly;
    this.currentHeadLevel = this.specialization && this.specialization.currentHeadRequiredLevel
      || (this.currentHeadEnabled ? null : SKILL_LEARNING_LEVEL_COUNT.ONE);
    this.initialCurrentHeadEnabled = this.currentHeadEnabled;
    this.initialCurrentHeadLevel = this.currentHeadLevel;
    this.currentHeadDirty = false;
  }

  private hasDepartmentRequirement(): boolean {
    return !!this.selectedDepartmentId
      && !!this.specialization
      && this.specialization.probationRequiredByDepartment === true;
  }

  private canSaveScope(enabled: boolean, initiallyEnabled: boolean, capability: boolean): boolean {
    return this.hasRevision && capability && !this.isSubmitting && !this.pendingScope
      && (enabled ? this.isPublished : initiallyEnabled);
  }

  private canEmitScope(scope: SkillTrainingRequirementScope, enabled: boolean): boolean {
    if (scope === this.scopes.CURRENT_HEAD) {
      return this.canSaveCurrentHead;
    }
    if (scope === this.scopes.COMPANY_PROBATION) {
      return this.canSaveScope(
        enabled,
        this.initialCompanyEnabled,
        enabled ? this.canSetCompany : this.canDeleteCompany,
      );
    }
    return scope === this.scopes.DEPARTMENT_PROBATION && !!this.selectedDepartmentId
      && this.canSaveScope(enabled, this.initialDepartmentEnabled, this.canManageDepartment);
  }

  private updateCompanyDirty(): void {
    this.companyDirty = this.companyEnabled !== this.initialCompanyEnabled
      || (this.companyEnabled && this.companyLevel !== this.initialCompanyLevel);
  }

  private updateCurrentHeadDirty(): void {
    this.currentHeadDirty = this.currentHeadEnabled !== this.initialCurrentHeadEnabled
      || (this.currentHeadEnabled && this.currentHeadLevel !== this.initialCurrentHeadLevel);
  }

  private updateDepartmentDirty(): void {
    this.departmentDirty = this.departmentEnabled !== this.initialDepartmentEnabled
      || (this.departmentEnabled && this.departmentLevel !== this.initialDepartmentLevel);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-requirements-editor/specialization-requirements-editor.component.ts